// Persistent offline outbox (IndexedDB). Every task mutation goes through here so the UI is
// optimistic, retries are idempotent, and pending/failed state is shown honestly.
import { openDB, type IDBPDatabase } from "idb";
import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import type { TaskWithFlags } from "@desk/contracts";
import { ApiRequestError, describeError, type ApiClient } from "./api";
import {
  backoffMs,
  buildOverlay,
  classify,
  emptyMeta,
  nextRunnable,
  nextWake,
  rebaseRevision,
  recordRevision,
  resolveId,
  type OutboxEntry,
  type OutboxMeta,
  type OutboxOp,
  type Overlay,
} from "./outbox-core";
import { connection } from "./connection";
import { invalidateTaskData, upsertTaskInCaches } from "./cache";

export type EnqueueResult =
  | { status: "synced"; task?: TaskWithFlags }
  | { status: "queued" }
  | { status: "failed"; error: ApiRequestError | Error }
  | { status: "conflict"; entry: OutboxEntry };

interface Snapshot {
  entries: OutboxEntry[];
  overlay: Overlay;
}

type DB = IDBPDatabase<unknown>;

class OutboxStore {
  private entries: OutboxEntry[] = [];
  private meta: OutboxMeta = emptyMeta();
  private snapshot: Snapshot = { entries: [], overlay: buildOverlay([], emptyMeta(), new Date().toISOString()) };
  private listeners = new Set<() => void>();
  private db: Promise<DB> | null = null;
  private api: ApiClient | null = null;
  private qc: QueryClient | null = null;
  private running = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private waiters = new Map<string, (r: EnqueueResult) => void>();
  private dropOnFailure = new Set<string>();
  private ready: Promise<void> | null = null;

  init(api: ApiClient, qc: QueryClient): Promise<void> {
    this.api = api;
    this.qc = qc;
    if (!this.ready) this.ready = this.load();
    void this.ready.then(() => this.kick());
    return this.ready;
  }

  private openDb(): Promise<DB> {
    if (!this.db) {
      this.db = openDB("desk-outbox", 1, {
        upgrade(db) {
          db.createObjectStore("entries", { keyPath: "id" });
          db.createObjectStore("meta");
        },
      });
    }
    return this.db;
  }

  private async load() {
    try {
      const db = await this.openDb();
      const entries = (await db.getAll("entries")) as OutboxEntry[];
      const meta = ((await db.get("meta", "meta")) as OutboxMeta | undefined) ?? emptyMeta();
      // A reload during "sending" means the result is unknown: retry (idempotency keys make it safe).
      this.entries = entries.map((e) => (e.state === "sending" ? { ...e, state: "pending" as const } : e));
      this.meta = meta;
      this.emit();
    } catch (e) {
      console.warn("[desk] outbox unavailable; changes will not survive reload", e);
    }
  }

  private async persist(entry?: OutboxEntry, removeId?: string) {
    try {
      const db = await this.openDb();
      if (entry) await db.put("entries", entry);
      if (removeId) await db.delete("entries", removeId);
      await db.put("meta", this.meta, "meta");
    } catch {
      /* keep in-memory */
    }
  }

  private emit() {
    this.snapshot = { entries: [...this.entries], overlay: buildOverlay(this.entries, this.meta, new Date().toISOString()) };
    this.listeners.forEach((l) => l());
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  getSnapshot = () => this.snapshot;

  /** Map a client id to its server id once known. */
  resolve(id: string) {
    return resolveId(id, this.meta);
  }

  async enqueue(op: OutboxOp, opts: { id?: string; awaitResult?: boolean; dropOnFailure?: boolean } = {}): Promise<EnqueueResult> {
    await this.ready;
    const id = opts.id ?? (op.type === "task.create" ? op.input.clientId : crypto.randomUUID());
    if (this.entries.some((e) => e.id === id)) {
      // Repeated submit of the same draft: never enqueue twice.
      return { status: "queued" };
    }
    const entry: OutboxEntry = { id, op, createdAt: Date.now(), attempts: 0, nextAttemptAt: 0, state: "pending" };
    this.entries.push(entry);
    if (opts.dropOnFailure) this.dropOnFailure.add(id);
    this.emit();
    await this.persist(entry);
    const result = opts.awaitResult && connection.get().online ? new Promise<EnqueueResult>((resolve) => this.waiters.set(id, resolve)) : null;
    this.kick();
    return result ?? { status: "queued" };
  }

  /** Retry everything now (called on reconnect). */
  retryAll() {
    const now = Date.now();
    this.entries = this.entries.map((e) => (e.state === "pending" ? { ...e, nextAttemptAt: now } : e));
    this.emit();
    this.kick();
  }

  async retry(id: string) {
    const e = this.entries.find((x) => x.id === id);
    if (!e) return;
    Object.assign(e, { state: "pending", nextAttemptAt: 0, attempts: 0, error: undefined });
    this.emit();
    await this.persist(e);
    this.kick();
  }

  async discard(id: string) {
    this.entries = this.entries.filter((e) => e.id !== id);
    this.emit();
    await this.persist(undefined, id);
    if (this.qc) void invalidateTaskData(this.qc);
    this.kick();
  }

  /** Conflict: re-apply the user's values on top of the latest server revision. */
  async useMine(id: string) {
    const e = this.entries.find((x) => x.id === id);
    if (!e) return;
    const current = e.current as { revision?: number } | undefined;
    if (e.op.type === "task.update") e.op = { ...e.op, patch: { ...e.op.patch, baseRevision: current?.revision } };
    if (e.op.type === "task.move") e.op = { ...e.op, baseRevision: current?.revision };
    Object.assign(e, { state: "pending", nextAttemptAt: 0, attempts: 0, error: undefined, current: undefined });
    this.emit();
    await this.persist(e);
    this.kick();
  }

  kick() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    void this.run();
  }

  private schedule() {
    const wake = nextWake(this.entries);
    if (wake === null) return;
    const delay = Math.max(250, wake - Date.now());
    this.timer = setTimeout(() => this.kick(), delay);
  }

  private async run() {
    if (this.running || !this.api) return;
    this.running = true;
    try {
      for (;;) {
        if (!connection.get().online) {
          for (const id of this.waiters.keys()) this.settle(id, { status: "queued" });
          break;
        }
        const entry = nextRunnable(this.entries, Date.now());
        if (!entry) break;
        await this.send(entry);
      }
    } finally {
      this.running = false;
      this.schedule();
    }
  }

  private settle(id: string, result: EnqueueResult) {
    const w = this.waiters.get(id);
    if (w) {
      this.waiters.delete(id);
      w(result);
    }
  }

  private async send(entry: OutboxEntry) {
    entry.state = "sending";
    entry.attempts += 1;
    this.emit();
    try {
      const task = await this.execute(entry.op, entry.id);
      connection.set({ unreachable: false });
      if (entry.op.type === "task.create" && task) {
        this.meta = { ...this.meta, idMap: { ...this.meta.idMap, [entry.op.input.clientId]: task.id } };
      }
      if (task) {
        this.meta = recordRevision(this.meta, task.id, task.revision);
        if (this.qc) upsertTaskInCaches(this.qc, task);
      }
      if (this.qc) await invalidateTaskData(this.qc);
      this.entries = this.entries.filter((e) => e.id !== entry.id);
      this.dropOnFailure.delete(entry.id);
      this.emit();
      await this.persist(undefined, entry.id);
      this.settle(entry.id, { status: "synced", task });
    } catch (err) {
      const e = err instanceof ApiRequestError ? err : new ApiRequestError({ status: 0, code: "network", message: String(err) });
      const outcome = classify(e);
      if (outcome === "retry") {
        if (e.code === "network") connection.set({ unreachable: true });
        entry.state = "pending";
        entry.nextAttemptAt = Date.now() + backoffMs(entry.attempts, Math.random());
        entry.error = describeError(e);
        this.emit();
        await this.persist(entry);
        this.settle(entry.id, { status: "queued" });
      } else if (outcome === "conflict") {
        entry.state = "conflict";
        entry.current = e.current;
        entry.error = describeError(e);
        this.emit();
        await this.persist(entry);
        this.settle(entry.id, { status: "conflict", entry });
      } else if (this.dropOnFailure.has(entry.id)) {
        this.dropOnFailure.delete(entry.id);
        this.entries = this.entries.filter((x) => x.id !== entry.id);
        this.emit();
        await this.persist(undefined, entry.id);
        this.settle(entry.id, { status: "failed", error: e });
      } else {
        entry.state = "failed";
        entry.error = describeError(e);
        this.emit();
        await this.persist(entry);
        this.settle(entry.id, { status: "failed", error: e });
      }
    }
  }

  private async execute(op: OutboxOp, key: string): Promise<TaskWithFlags | undefined> {
    const api = this.api!;
    const id = "taskId" in op ? resolveId(op.taskId, this.meta) : "";
    switch (op.type) {
      case "task.create":
        return api.createTask(op.input, key);
      case "task.update": {
        const baseRevision = rebaseRevision(id, op.patch.baseRevision, this.meta);
        return api.updateTask(id, { ...op.patch, baseRevision }, key);
      }
      case "task.action":
        return api.taskAction(id, op.action, key);
      case "task.snooze":
        return api.snoozeTask(id, op.until, key);
      case "task.move":
        return api.moveTask(id, { ...op.dest, baseRevision: rebaseRevision(id, op.baseRevision, this.meta) }, key);
      case "plan.add":
        await api.planAdd(op.date, op.taskIds.map((t) => resolveId(t, this.meta)), key);
        return undefined;
      case "plan.remove":
        await api.planRemove(op.date, resolveId(op.taskId, this.meta), key);
        return undefined;
      case "checklist.add":
        return api.checklistAdd(id, op.title, op.clientId);
      case "checklist.update":
        return api.checklistUpdate(id, op.itemId, op.patch);
      case "checklist.delete":
        return api.checklistDelete(id, op.itemId);
      case "checklist.reorder":
        return api.checklistReorder(id, op.ids);
    }
  }
}

export const outbox = new OutboxStore();

export function useOutbox(): Snapshot {
  return useSyncExternalStore(outbox.subscribe, outbox.getSnapshot, outbox.getSnapshot);
}

export function useOverlay(): Overlay {
  return useOutbox().overlay;
}
